import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashBoardDialog from './DashBoardDialog';
import { StashSamInitialTabInSession } from '../../../Scripts/SafeWork/SafetyWorkManagement/SAM_BASIC/samInitialTabSession';
import dayjs from 'dayjs';

/** 탭 라벨은 상세 목록 `item.title`에 포함 여부로 필터(전체 제외) */
export const CHECKLIST_DIALOG_TABS = [
  '전체',
  '안전신문고',
  '작업허가서',
  '작업종료',
  '작업중지',
];

/**
 * 대시보드 확인사항 행 `item.title` → ChecklistDialog 탭 인덱스
 * (긴 라벨 우선 — '작업'만 겹치는 경우 방지)
 */
export function resolveAlarmTitleToChecklistTabIndex(
  title,
  tabs = CHECKLIST_DIALOG_TABS
) {
  const t = String(title ?? '').trim();
  if (!t) return 0;
  for (let i = 1; i < tabs.length; i++) {
    const label = tabs[i];
    if (label && t === label) return i;
  }
  const matches = [];
  for (let i = 1; i < tabs.length; i++) {
    const label = tabs[i];
    if (label && t.includes(label)) matches.push({ i, len: label.length });
  }
  if (matches.length === 0) return 0;
  matches.sort((a, b) => b.len - a.len);
  return matches[0].i;
}

/**
 * 확인사항 행 `item.title` → 라우터 이동 대상 (매칭 실패 시 null)
 * 작업허가: `/SafetyWorkManagement` + navigate state `{ samInitialTab: 1 }` (SAM_BASICF와 키 일치)
 */
export function ResolveAlarmTitleToNavigateTarget(title) {
  const idx = resolveAlarmTitleToChecklistTabIndex(title);
  if (idx === 1) return { pathname: '/SuggestionList', search: '' };
  if (idx === 2) {
    return {
      pathname: '/SafetyWorkManagement',
      search: '',
      state: { samInitialTab: 1 },
    };
  }
  return null;
}

function ChecklistDialog({
  open = false,
  onClose = () => {},
  onReadAll = () => {},
  items = [],
  tabs = CHECKLIST_DIALOG_TABS,
  /** 대시보드에서 행 클릭 시 전달 — 다이얼로그가 열릴 때만 적용 */
  initialActiveTabIndex = 0,
  handleSaveLogic4,
}) {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(0);
  const prevOpenRef = useRef(false);

  const NavigateLogicN = (item) => {
    const target = ResolveAlarmTitleToNavigateTarget(item?.title);
    if (!target) return;
    if (
      target.pathname === '/SafetyWorkManagement' &&
      target.state?.samInitialTab === 1
    ) {
      StashSamInitialTabInSession(1);
    }
    onClose();
    if (target.state != null) {
      navigate(target.pathname, { state: target.state });
    } else {
      navigate(target.pathname);
    }
  };

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      const n = Number(initialActiveTabIndex);
      const safe = Number.isInteger(n) && n >= 0 && n < tabs.length ? n : 0;
      setActiveTab(safe);
    }
    prevOpenRef.current = open;
  }, [open, initialActiveTabIndex, tabs.length]);

  const filtered =
    activeTab === 0
      ? items
      : items.filter(
          (item) => item.title && item.title.includes(tabs[activeTab])
        );

  const fields = [
    {
      label: '',
      span: 2,
      value: (
        <div className="w-full mx-0 border border-[#e1e8ed] rounded-lg overflow-hidden">
          <div className="grid grid-cols-[2fr_3fr_1.5fr_1fr] px-3 pr-5 py-2.5 bg-gray-100 border-b border-[#e1e8ed]">
            <span className="text-[11px] font-bold text-[#7f8c8d] text-left ">
              제목
            </span>
            <span className="text-[11px] font-bold text-[#7f8c8d] text-left ">
              내용
            </span>
            <span className="text-[11px] font-bold text-[#7f8c8d] text-center">
              날짜
            </span>
            <span className="text-[11px] font-bold text-[#7f8c8d] text-center">
              읽음 처리
            </span>
          </div>

          <div
            className="h-80 overflow-y-auto divide-y divide-[#e1e8ed]/60 custom-scrollbar bg-white"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#e1e8ed transparent',
            }}
          >
            {filtered.length === 0 ? (
              <div className="flex items-center justify-center h-full text-sm text-gray-400">
                항목이 없습니다.
              </div>
            ) : (
              filtered.map((item) => {
                const canRowNavigate = !!ResolveAlarmTitleToNavigateTarget(
                  item?.title
                );
                return (
                  <div
                    key={item.id}
                    role={canRowNavigate ? 'button' : undefined}
                    tabIndex={canRowNavigate ? 0 : undefined}
                    onClick={
                      canRowNavigate ? () => NavigateLogicN(item) : undefined
                    }
                    onKeyDown={
                      canRowNavigate
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              NavigateLogicN(item);
                            }
                          }
                        : undefined
                    }
                    className={`grid grid-cols-[2fr_3fr_1.5fr_1fr] px-3 py-3.5 items-center transition-colors ${
                      canRowNavigate
                        ? 'cursor-pointer hover:bg-gray-50'
                        : 'cursor-default'
                    } ${item.useYn === 'Y' ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-center gap-2 pr-2 overflow-hidden">
                      <div className="w-2 shrink-0">
                        {item.useYn !== 'Y' && (
                          <span className="w-2 h-2 rounded-full bg-[#77b054] block" />
                        )}
                      </div>
                      <span
                        className={`text-sm truncate ${item.useYn === 'Y' ? 'font-medium text-gray-500' : 'font-bold text-[#2c3e50]'}`}
                      >
                        {item.title}
                      </span>
                    </div>

                    <div className="text-sm text-gray-400 truncate pr-4 text-left">
                      {item.comment}
                    </div>

                    <div className="text-xs text-gray-400 text-center">
                      {dayjs(item.createDate).format('YYYY-MM-DD HH:mm')}
                    </div>

                    <div className="flex justify-center items-center">
                      {item.useYn === 'Y' ? (
                        <span className="px-3 py-1 bg-gray-100 text-gray-400 text-[11px] font-bold rounded-full min-w-16.25 text-center">
                          확인완료
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveLogic4(item);
                          }}
                          className="px-3 py-1.5 bg-[#77b054] text-white text-xs font-bold rounded-lg hover:bg-[#5d9040] transition-colors active:scale-95 min-w-16.25"
                        >
                          읽음
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <DashBoardDialog
      open={open}
      onClose={onClose}
      titleIcon={
        <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center text-[#77b054]">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
        </div>
      }
      title="확인사항 리스트"
      fields={fields}
      size="xl"
      headerExtra={
        <div className="flex gap-6 border-b border-[#e1e8ed] -mt-1">
          {tabs.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              className={`pb-2.5 px-0.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === i
                  ? 'text-[#2c3e50] font-bold border-[#2c3e50]'
                  : 'text-gray-400 border-transparent hover:text-gray-600'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      }
      actions={
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-3.5 h-3.5 text-blue-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            미확인 항목이 존재할 시 대시보드 알림이 지속됩니다.
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-lg text-sm font-bold bg-white text-[#7f8c8d] border border-[#e1e8ed] hover:bg-gray-100 transition-colors"
            >
              닫기
            </button>
            <button
              onClick={onReadAll} // 전체 읽음 API 호출 (Gubun: 2)
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-bold bg-[#77b054] text-white hover:bg-[#5d9040] transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6L9 17l-5-5" />
                <path d="M15 6l-6 6" />
              </svg>
              전체 읽음
            </button>
          </div>
        </div>
      }
    />
  );
}

export default ChecklistDialog;
